
import React, { useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Plus, Edit, Trash2 } from "lucide-react";
import { useFAQs, FAQ, FAQInsert, FAQUpdate } from "./useFAQs";
import FAQForm from "./FAQForm";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";

const FAQsList: React.FC = () => {
  const { faqs, isLoading, error, createFAQ, updateFAQ, deleteFAQ } = useFAQs();
  
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedFAQ, setSelectedFAQ] = useState<FAQ | undefined>(undefined);

  const handleOpenCreateForm = () => {
    setSelectedFAQ(undefined);
    setIsFormOpen(true);
  };

  const handleOpenEditForm = (faq: FAQ) => {
    setSelectedFAQ(faq);
    setIsFormOpen(true);
  };

  const handleOpenDeleteDialog = (faq: FAQ) => {
    setSelectedFAQ(faq);
    setIsDeleteDialogOpen(true);
  };

  const handleSaveFAQ = async (data: FAQInsert | FAQUpdate) => {
    setIsProcessing(true);
    try {
      if (selectedFAQ) {
        await updateFAQ(selectedFAQ.id, data);
      } else {
        await createFAQ(data as FAQInsert);
      }
      setIsFormOpen(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteFAQ = async () => {
    if (!selectedFAQ) return;
    
    setIsProcessing(true);
    try {
      await deleteFAQ(selectedFAQ.id);
      setIsDeleteDialogOpen(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="border rounded-md p-4 space-y-4">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mb-6">
        <AlertTitle>Erro</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    
    try {
      return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm", {
        locale: ptBR
      });
    } catch (err) {
      return "Data inválida";
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-toctoc-black">
          Perguntas Frequentes
        </h1>
        <Button 
          className="bg-toctoc-orange hover:bg-orange-600"
          onClick={handleOpenCreateForm}
        >
          <Plus className="mr-2 h-4 w-4" /> Nova FAQ
        </Button>
      </div>

      {faqs.length === 0 ? (
        <div className="text-center p-8 border rounded-md bg-gray-50">
          <p className="text-gray-600">Nenhuma pergunta frequente cadastrada.</p>
          <Button 
            className="mt-4 bg-toctoc-orange hover:bg-orange-600"
            onClick={handleOpenCreateForm}
          >
            <Plus className="mr-2 h-4 w-4" /> Adicionar Primeira FAQ
          </Button>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Categoria</TableHead>
              <TableHead>Pergunta</TableHead>
              <TableHead>Resposta</TableHead>
              <TableHead>Última Atualização</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {faqs.map((faq) => (
              <TableRow key={faq.id}>
                <TableCell>{faq.category}</TableCell>
                <TableCell className="font-medium">{faq.question}</TableCell>
                <TableCell>{truncateText(faq.answer, 80)}</TableCell>
                <TableCell>{formatDate(faq.updated_at)}</TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenEditForm(faq)}
                    >
                      <Edit className="h-4 w-4 text-toctoc-blue" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDeleteDialog(faq)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>
              {selectedFAQ ? "Editar" : "Adicionar"} Pergunta Frequente
            </DialogTitle>
            <DialogDescription>
              Preencha os campos abaixo com as informações da pergunta frequente.
            </DialogDescription>
          </DialogHeader>
          <FAQForm
            initial={selectedFAQ}
            onSave={handleSaveFAQ}
            onCancel={() => setIsFormOpen(false)}
            isProcessing={isProcessing}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Você tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. A pergunta 
              "{selectedFAQ?.question}" será permanentemente excluída 
              do sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isProcessing}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteFAQ}
              disabled={isProcessing}
              className="bg-red-500 hover:bg-red-600"
            >
              {isProcessing ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default FAQsList;
